import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchProductById } from '../api/woocommerce';
import { useTranslation } from 'react-i18next';

// Helper function to submit a review
const submitReview = async (productId, reviewData) => {

  try {
    const url = `${process.env.REACT_APP_WORDPRESS_URL}/wp-json/institute-lab/v1/review`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        product_id: productId,
        ...reviewData
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Failed to submit review: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error submitting review:', error);
    throw error;
  }
};

// SVG components
const FilledStar = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0L15.705 7.905L24 9.165L18 15.315L19.41 24L12 19.905L4.59 24L6 15.315L0 9.165L8.295 7.905L12 0Z" />
  </svg>
);

const OutlinedStar = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3.525L14.355 8.535L14.7 9.255L15.495 9.375L20.895 10.2L16.935 14.265L16.41 14.805L16.53 15.555L17.445 21.195L12.735 18.585L12.015 18.18L11.295 18.585L6.585 21.195L7.5 15.555L7.62 14.805L7.095 14.265L3.135 10.2L8.535 9.375L9.33 9.255L9.675 8.535L12.03 3.525M12 0L8.295 7.905L0 9.165L6 15.315L4.59 24L12 19.905L19.41 24L18 15.315L24 9.165L15.705 7.905L12 0Z" />
  </svg>
);

// StarRating component
const StarRating = ({ rating, onRatingChange, interactive = false }) => {
  const [hoverRating, setHoverRating] = useState(0);

  const handleMouseEnter = (index) => {
    if (interactive) {
      setHoverRating(index);
    }
  };

  const handleMouseLeave = () => {
    if (interactive) {
      setHoverRating(0);
    }
  };

  const handleClick = (index) => {
    if (interactive && onRatingChange) {
      onRatingChange(index);
    }
  };

  return (
    <div className="flex items-center">
      {[1, 2, 3, 4, 5].map((index) => {
        const isFilled = index <= (hoverRating || rating);
        return (
          <span
            key={index}
            className={`cursor-${interactive ? 'pointer' : 'default'} mr-2`}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
            onClick={() => handleClick(index)}
          >
            {isFilled ? <FilledStar /> : <OutlinedStar />}
          </span>
        );
      })}
    </div>
  );
};


const ReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewFormData, setReviewFormData] = useState({
    reviewer: '',
    reviewer_email: '',
    review: '',
    rating: 0, // Changed from 4 to 0 to start with empty stars
    order_number: '',
    surname: '',
    recommend: 'yes',
    headline: ''
  });
  const { t } = useTranslation();

  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewSubmitError, setReviewSubmitError] = useState(null);
  const [reviewSubmitSuccess, setReviewSubmitSuccess] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        const productData = await fetchProductById(id);
        setProduct(productData);
      } catch (err) {
        console.error('Error loading product:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadProduct();
    }
  }, [id]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReviewFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  // Handle star rating selection
  const handleRatingChange = (rating) => {
    setReviewFormData(prevData => ({
      ...prevData,
      rating
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (reviewSubmitted) {
      return;
    }

    setSubmittingReview(true);
    setReviewSubmitError(null);
    setReviewSubmitSuccess(false);
    setReviewSubmitted(true);
    try {
      const reviewData = {
        reviewer: reviewFormData.reviewer,
        reviewer_email: reviewFormData.reviewer_email,
        review: reviewFormData.review,
        rating: reviewFormData.rating,
        headline: reviewFormData.headline,
        recommend: reviewFormData.recommend,
        product_id: parseInt(id, 10)
      };

      await submitReview(id, reviewData);

      setReviewSubmitSuccess(true);

      // Redirect back to product page after 3 seconds
      setTimeout(() => {
        navigate(`/store/product/${product.slug}`);
      }, 3000);
    } catch (err) {
      setReviewSubmitError(err.message || 'Failed to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-[64px] text-center">
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-[64px] text-center">
        <p className="text-lg text-red-500">Product not found</p>
      </div>
    );
  }



  return (
    <div className='mx-2'>
      <div className="container mx-auto pb-[64px]">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-26 font-light mb-2">{t('product.reviewPurchase')}</h1>
          <h2 className="text-26 font-normal mb-4">{product.name.toLowerCase()}</h2>

          <form onSubmit={handleSubmit} className="space-y-2">
            {/* Name and Surname Row */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <input
                  type="text"
                  name="reviewer"
                  placeholder={t('product.form.namePlaceholder')}
                  className="placeholder-[#BDBDBD] placeholder:text-14 placeholder:font-normal w-full border border-black p-3 h-9 text-14 font-normal"
                  value={reviewFormData.reviewer}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <input
                  type="text"
                  name="surname"
                  placeholder={t('product.form.surnamePlaceholder')}
                  className="placeholder-[#BDBDBD] placeholder:text-14 placeholder:font-normal w-full border border-black p-3 h-9 text-14 font-normal"
                  value={reviewFormData.surname}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            {/* Email and Order Number Row */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <input
                  type="email"
                  name="reviewer_email"
                  placeholder={t('product.form.emailPlaceholder')}
                  className="placeholder-[#BDBDBD] placeholder:text-14 placeholder:font-normal w-full border border-black p-3 h-9 text-14 font-normal"
                  value={reviewFormData.reviewer_email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <input
                  type="text"
                  name="order_number"
                  placeholder={t('product.form.orderNumberPlaceholder')}
                  className="placeholder-[#BDBDBD] placeholder:text-14 placeholder:font-normal w-full border border-black p-3 h-9 text-14 font-normal"
                  value={reviewFormData.order_number}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            {/* Rating Section */}
            <div className="mt-2">
              <p className="text-18 font-light mb-2">{t('product.rateProduct')}</p>
              <StarRating
                rating={reviewFormData.rating}
                onRatingChange={handleRatingChange}
                interactive={true}
              />
              <hr className="border-t border-black mt-[17px]" />
            </div>

            {/* Headline Section */}
            <div className="mt-2">
              <p className="text-18 font-light mb-2">{t('product.addHeadline')}</p>
              <input
                type="text"
                name="headline"
                placeholder={t('product.form.headlinePlaceholder')}
                className="placeholder-[#BDBDBD] placeholder:text-14 placeholder:font-normal w-full border border-black p-3 h-9 text-14 font-normal"
                value={reviewFormData.headline}
                onChange={handleInputChange}
                required
              />
            </div>

            {/* Review Text */}
            <div className="mt-2">
              <p className="mb-2 text-18 font-light mt-2">{t('product.tellMore')}</p>
              <textarea
                name="review"
                placeholder={t('product.reviewPlaceholder')}
                className="placeholder-[#BDBDBD] placeholder:text-14 placeholder:font-normal w-full border border-black p-3 text-14 font-normal resize-none block box-border"
                style={{ height: '120px', minHeight: '120px', maxHeight: '120px' }}
                value={reviewFormData.review}
                onChange={handleInputChange}
                required
              ></textarea>
            </div>

            {/* Recommendation Buttons */}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button
                type="button"
                className={`p-3 text-14 border h-9 flex items-center justify-center ${reviewFormData.recommend === 'yes' ? 'border-black font-normal' : 'border-[#BDBDBD] font-light'}`}
                onClick={() => setReviewFormData(prev => ({ ...prev, recommend: 'yes' }))}
              >
                {t('product.wouldRecommend')}
              </button>
              <button
                type="button"
                className={`p-3 text-14 border h-9 flex items-center justify-center ${reviewFormData.recommend === 'no' ? 'border-black font-normal' : 'border-[#BDBDBD] font-light'}`}
                onClick={() => setReviewFormData(prev => ({ ...prev, recommend: 'no' }))}
              >
                {t('product.wouldNotRecommend')}
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full py-3 bg-black text-white font-normal text-14 h-9 flex items-center justify-center mt-2"
              disabled={reviewSubmitted}
            >
              {submittingReview ? 'Submitting...' : t('product.submitReviewButton')}
            </button>

            {reviewSubmitError && (
              <p className="text-red-500 text-sm mt-2">{reviewSubmitError}</p>
            )}

            {reviewSubmitSuccess && (
              <p className="text-black text-14 font-normal text-center mt-2">Thank you for submitting your review.</p>
            )}
          </form>
        </div>
      </div></div>
  );
};

export default ReviewPage;